import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../../assets/images';
import Loader from '../../components/common/Loader';
import chargebeeService from '../../services/chargebee.service';
import { decodeIdToken, decodeMetadataToken } from '../../utils/token.utils';
import ExperienceSelectionStep from './steps/ExperienceSelectionStep';
import GoalSelectionStep from './steps/GoalSelectionStep';
import OnboardingSourceStep from './steps/OnboardingSourceStep';
import PlanActivationStep from './steps/PlanActivationStep';
import RoleSelectionStep from './steps/RoleSelectionStep';
import SignupStepCard from './steps/SignupStepCard';
import { getCookie, removeCookie } from '../../utils/cookies.utils';

interface OnboardingFlowState {
  step: number;
  email: string;
  role: string;
  goals: string[];
  experience: string[];
  leadSource: string;
  ghlContactId: string;
}

const fallbackPlanId = 'All-In-One-USD-Yearly-200';

export default function OnboardingFlow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [flow, setFlow] = useState<OnboardingFlowState>(() => {
    const idToken = decodeIdToken();
    const emailToUse = idToken?.email || '';
    return { step: 1, email: emailToUse, role: '', goals: [], experience: [], leadSource: '', ghlContactId: '' };
  });
  const [currentPlan, setCurrentPlan] = useState('');
  const [isTrial, setIsTrial] = useState('');
  const planId = searchParams.get('planId') || fallbackPlanId;
  const { step, email, role, goals, experience, leadSource, ghlContactId } = flow;

  const emailInitial = (email.trim()[0] || '').toUpperCase();

  const isTrialPlan = currentPlan.toLowerCase().includes('trial') || isTrial === 'in_trial';
  const showCardSection = isTrialPlan;
  const hasPendingOnboarding = !!getCookie('hasPendingOnboarding');

 
  const handleCardSubmit = async (cardTokenResult: string) => {
    const metadata = decodeMetadataToken();
    const customerId = metadata?.companies?.[0]?.id || '';
    if (!customerId) {
      throw new Error('Instance not created');
    }

    const cardResponse = await chargebeeService.addPaymentSource(customerId, cardTokenResult);
    if (!cardResponse || cardResponse.error) {
      throw new Error('Failed to add payment source');
    }

    const subscriptionId = metadata?.companies?.[0]?.subscription?.id || '';
    if (!subscriptionId) {
      throw new Error('Subscription not found');
    }
    const planMatch = planId.match(/^(.+)-(\d+)$/);
    if (!planMatch) {
      throw new Error('Invalid planId');
    }
    const basePlanId = planMatch[1];
    const planUnits = parseInt(planMatch[2], 10);
    await chargebeeService.changePlan(subscriptionId, basePlanId, planUnits);
    removeCookie('hasPendingOnboarding');
    navigate('/dashboard/overview?redirect=easywebinar');

  };

  useEffect(() => {
    if (!searchParams.get('planId')) {
      setSearchParams((prev) => {
        prev.set('planId', fallbackPlanId);
        return prev;
      }, { replace: true });
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    const fetchSubscriptionDetails = async () => {
      try {
        const metadata = decodeMetadataToken();
        const customerId = metadata?.companies?.[0]?.id;
        if (!customerId) return;

        const response = await chargebeeService.getSubscriptionDetails(customerId);
        const subscriptions = response?.data[0] || [];
        // const activePlanSubscription =
        //   subscriptions.find(
        //     (item: any) =>
        //       ['active'].includes(item?.subscription?.status) &&
        //       item?.subscription_items?.some((subItem: any) => subItem?.itemType === 'plan')
        //   ) || subscriptions[0];

        const planItem = subscriptions?.subscription_items?.find(
          (item: any) => item?.itemType === 'plan'
        );
        const itemPriceId = planItem?.itemPriceId || '';
        const isTrialPlanItem = itemPriceId.toLowerCase().includes('trial');
        setCurrentPlan(itemPriceId);
        setIsTrial(isTrialPlanItem);
      } catch {
        setCurrentPlan('');
        setIsTrial('');
      } finally{
        setIsLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, []);



  console.log("step",step);
  
  if (isLoading) return <Loader fullHeight minHeight='100vh' />;
  const renderStep = () => {
    const roleStep = 1;
    const goalStep = roleStep + 1;
    const experienceStep = goalStep + 1;
    const leadSourceStep = experienceStep + 1;
    const activationStep = leadSourceStep + 1;
    const cardStep = activationStep + 1;

    if (step === cardStep && showCardSection) {
      return (
        <SignupStepCard
          planId={planId}
          onSubmit={handleCardSubmit}
          onSkip={() => navigate('/dashboard/overview?redirect=easywebinar')}
        />
      );
    }

    if (step === roleStep) {
      return (
        <RoleSelectionStep
          email={email}
          initialSelected={role}
          onComplete={(selectedRole) =>
            setFlow((p) => ({ ...p, role: selectedRole, step: goalStep, ghlContactId: ghlContactId }))
          }
          onSkip={() => setFlow((p) => ({ ...p, role: '', step: goalStep, ghlContactId: ghlContactId }))}
          onBack={() => setFlow((p) => ({ ...p, step: Math.max(roleStep - 1, 1) }))}
          ghlContactId={ghlContactId}
        />
      );
    }

    if (step === goalStep) {
      return (
        <GoalSelectionStep
          email={email}
          initialSelected={goals}
          onComplete={(selectedGoals) =>
            setFlow((p) => ({ ...p, goals: selectedGoals, step: experienceStep, ghlContactId: ghlContactId }))
          }
          onSkip={() => setFlow((p) => ({ ...p, goals: [], step: experienceStep, ghlContactId: ghlContactId }))}
          onBack={() => setFlow((p) => ({ ...p, step: roleStep }))}
          ghlContactId={ghlContactId}
        />
      );
    }

    if (step === experienceStep) {
      return (
        <ExperienceSelectionStep
          email={email}
          initialSelected={experience}
          onComplete={(exp) => setFlow((p) => ({ ...p, experience: exp, step: leadSourceStep, ghlContactId: ghlContactId }))}
          onSkip={() => setFlow((p) => ({ ...p, experience: [], step: leadSourceStep, ghlContactId: ghlContactId }))}
          onBack={() => setFlow((p) => ({ ...p, step: goalStep, ghlContactId: ghlContactId }))}
          ghlContactId={ghlContactId}
        />
      );
    }

    if (step === leadSourceStep) {
      return (
        <OnboardingSourceStep
          email={email}
          initialSelected={leadSource}
          onComplete={(selectedLeadSource: string, updatedGhlContactId: string) => 
            setFlow((p) => ({ ...p, leadSource: selectedLeadSource, step: activationStep, ghlContactId: updatedGhlContactId }))
          }
          onSkip={() => setFlow((p) => ({ ...p, leadSource: '', step: activationStep, ghlContactId: ghlContactId }))}
          onBack={() => setFlow((p) => ({ ...p, step: experienceStep, ghlContactId: ghlContactId }))}
          ghlContactId={ghlContactId}
        />
      );
    }

    if (step === activationStep) {
      return (
        <PlanActivationStep
          onComplete={() =>
            showCardSection
              ? setFlow((p) => ({ ...p, step: cardStep, ghlContactId: ghlContactId }))
              : navigate('/dashboard/overview?redirect=easywebinar')
          }
          onBack={() => setFlow((p) => ({ ...p, step: leadSourceStep, ghlContactId: ghlContactId }))}
          ghlContactId={ghlContactId}
        />
      );
    }

    return null;
  };
  if (!hasPendingOnboarding) {
    return <Navigate to="/dashboard/overview" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: '16px 24px', sm: '24px 32px' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={Logo}
            alt="logo"
            style={{ height: 24, cursor: 'pointer', maxWidth: '100%' }}
            onClick={() => window.open('https://easywebinar.com/', '_blank')}
          />
        </Box>
        <Box
          aria-label="signup-email-initial"
          sx={{
            height: 36,
            width: 36,
            borderRadius: '999px',
            bgcolor: 'rgba(96, 102, 233, 0.12)',
            color: '#6066E9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '0.95rem',
            userSelect: 'none',
          }}
        >
          {emailInitial || 'U'}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          px: 2,
          py: 4,
        }}
      >
        {/* <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: isCardStep ? 1400 : isActivationStep ? { xs: '100%', sm: 780 } : 480,
            borderRadius: '20px',
            // border: '1px solid rgba(16, 24, 40, 0.08)',
            // boxShadow: '0 24px 64px rgba(16, 24, 40, 0.16)',
            px: isCardStep ? 0 : { xs: 2.5, sm: 4 },
            py: isCardStep ? 0 : { xs: 3, sm: 4.5 },
            bgcolor: '#FFFFFF',
          }}
        > */}
          {renderStep()}
        {/* </Paper> */}
      </Box>
    </Box>
  );
}
